import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Package,
  Tag, ArrowUpRight, Loader2, RefreshCw, Calendar, Award, Zap
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#1f5f33', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  
  const [overview, setOverview] = useState(null);
  const [salesChart, setSalesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [promoPerformance, setPromoPerformance] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [subjectBreakdown, setSubjectBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartDays, setChartDays] = useState("7");

  useEffect(() => {
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) fetchSalesChart();
  }, [chartDays]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOverview(),
        fetchSalesChart(),
        fetchTopProducts(),
        fetchPromoPerformance(),
        fetchRecentOrders(),
        fetchSubjectBreakdown()
      ]);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    const response = await axios.get(`${API}/admin/analytics/overview`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setOverview(response.data);
  };

  const fetchSalesChart = async () => {
    const response = await axios.get(`${API}/admin/analytics/sales-chart?days=${chartDays}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSalesChart(response.data);
  };

  const fetchTopProducts = async () => {
    const response = await axios.get(`${API}/admin/analytics/top-products?limit=5`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setTopProducts(response.data);
  };

  const fetchPromoPerformance = async () => {
    const response = await axios.get(`${API}/admin/analytics/promo-performance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setPromoPerformance(response.data);
  };

  const fetchRecentOrders = async () => {
    const response = await axios.get(`${API}/admin/analytics/recent-orders?limit=5`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setRecentOrders(response.data);
  };

  const fetchSubjectBreakdown = async () => {
    const response = await axios.get(`${API}/admin/analytics/subject-breakdown`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSubjectBreakdown(response.data);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="analytics-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground mt-1">Track sales, performance, and trends</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchAllData} className="rounded-xl">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => navigate("/admin")} className="rounded-xl bg-primary">
              Back to Admin
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { 
              label: "Total Revenue", 
              value: formatCurrency(overview?.total_revenue || 0),
              subValue: `${formatCurrency(overview?.today_revenue || 0)} today`,
              icon: DollarSign, 
              color: "bg-success/10 text-success",
              trend: overview?.today_revenue > 0 ? "up" : "neutral"
            },
            { 
              label: "Total Orders", 
              value: overview?.total_orders || 0,
              subValue: `${overview?.today_orders || 0} today`,
              icon: ShoppingCart, 
              color: "bg-primary/10 text-primary",
              trend: overview?.today_orders > 0 ? "up" : "neutral"
            },
            { 
              label: "Avg Order Value", 
              value: formatCurrency(overview?.avg_order_value || 0),
              subValue: `${overview?.conversion_rate || 0}% conversion`,
              icon: TrendingUp, 
              color: "bg-accent/10 text-accent",
              trend: "neutral"
            },
            { 
              label: "Total Users", 
              value: overview?.total_users || 0,
              subValue: `${overview?.promo_orders || 0} used promos`,
              icon: Users, 
              color: "bg-blue-500/10 text-blue-500",
              trend: "neutral"
            }
          ].map((stat, i) => (
            <Card key={i} className="border-0 shadow-soft rounded-3xl overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-2xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  {stat.trend === "up" && (
                    <Badge className="bg-success/10 text-success border-0">
                      <ArrowUpRight className="w-3 h-3 mr-1" />
                      Live
                    </Badge>
                  )}
                </div>
                <p className="text-3xl font-bold mt-4">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                <p className="text-xs text-muted-foreground mt-2">{stat.subValue}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Sales Chart */}
          <Card className="lg:col-span-2 border-0 shadow-soft rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Sales Overview</CardTitle>
                <p className="text-sm text-muted-foreground">Revenue & orders over time</p>
              </div>
              <Select value={chartDays} onValueChange={setChartDays}>
                <SelectTrigger className="w-32 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesChart}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1f5f33" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#1f5f33" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#888" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '12px', 
                        border: 'none', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                      }}
                      formatter={(value, name) => [
                        name === 'revenue' ? formatCurrency(value) : value,
                        name === 'revenue' ? 'Revenue' : 'Orders'
                      ]}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#1f5f33" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Subject Breakdown */}
          <Card className="border-0 shadow-soft rounded-3xl">
            <CardHeader>
              <CardTitle className="font-heading">Sales by Subject</CardTitle>
              <p className="text-sm text-muted-foreground">Product category breakdown</p>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subjectBreakdown.slice(0, 6)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {subjectBreakdown.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-4">
                {subjectBreakdown.slice(0, 4).map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                      <span className="truncate max-w-[120px]">{item.subject}</span>
                    </div>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Top Products */}
          <Card className="border-0 shadow-soft rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Award className="w-5 h-5 text-accent" />
                  Top Products
                </CardTitle>
                <p className="text-sm text-muted-foreground">Best sellers</p>
              </div>
            </CardHeader>
            <CardContent>
              {topProducts.length > 0 ? (
                <div className="space-y-4">
                  {topProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-accent/10 text-accent' :
                          index === 1 ? 'bg-gray-200 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-600' :
                          'bg-secondary text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[150px]">{product.title}</p>
                          <p className="text-xs text-muted-foreground">{product.total_sold} sold</p>
                        </div>
                      </div>
                      <span className="font-bold text-primary">{formatCurrency(product.total_revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No sales data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Promo Performance */}
          <Card className="border-0 shadow-soft rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" />
                  Promo Performance
                </CardTitle>
                <p className="text-sm text-muted-foreground">Code usage stats</p>
              </div>
            </CardHeader>
            <CardContent>
              {promoPerformance.length > 0 ? (
                <div className="space-y-4">
                  {promoPerformance.slice(0, 5).map((promo, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <p className="font-mono font-bold text-primary">{promo.code}</p>
                        <p className="text-xs text-muted-foreground">Used {promo.times_used}x</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(promo.revenue_generated)}</p>
                        <p className="text-xs text-muted-foreground">-{formatCurrency(promo.total_discount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Tag className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No promo usage yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card className="border-0 shadow-soft rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Zap className="w-5 h-5 text-success" />
                  Recent Orders
                </CardTitle>
                <p className="text-sm text-muted-foreground">Latest transactions</p>
              </div>
            </CardHeader>
            <CardContent>
              {recentOrders.length > 0 ? (
                <div className="space-y-4">
                  {recentOrders.map((order, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="font-medium truncate max-w-[150px]">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{order.items_count} item(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{formatCurrency(order.total_amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No orders yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Bar */}
        <Card className="mt-8 border-0 shadow-soft rounded-3xl bg-gradient-to-r from-primary to-primary/80 text-white">
          <CardContent className="py-6">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-white/70 text-sm">This Week</p>
                <p className="text-2xl font-bold">{formatCurrency(overview?.week_revenue || 0)}</p>
                <p className="text-white/70 text-sm">{overview?.week_orders || 0} orders</p>
              </div>
              <div className="hidden md:block h-16 w-px bg-white/20" />
              <div>
                <p className="text-white/70 text-sm">Total Discounts Given</p>
                <p className="text-2xl font-bold">{formatCurrency(overview?.total_discounts || 0)}</p>
                <p className="text-white/70 text-sm">{overview?.promo_orders || 0} promo orders</p>
              </div>
              <div className="hidden md:block h-16 w-px bg-white/20" />
              <div>
                <p className="text-white/70 text-sm">Active Products</p>
                <p className="text-2xl font-bold">{overview?.total_products || 0}</p>
                <p className="text-white/70 text-sm">in catalog</p>
              </div>
              <Button variant="secondary" className="rounded-xl text-primary" onClick={() => navigate("/admin")}>
                Manage Products
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
